import { cn } from '../../lib/utils';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { useEffect, useState } from 'react';
import { services } from '../../services';

interface MetricProps {
  label: string;
  value: string;
  change?: number;
  prefix?: string;
  suffix?: string;
}

interface HedgeFundMetricsProps {
  className?: string;
}

export function HedgeFundMetrics({ className }: HedgeFundMetricsProps) {
  const [metrics, setMetrics] = useState<MetricProps[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: {
          nav: { value: number; change: number };
          mtdReturn: { value: number; change: number };
          ytdReturn: { value: number; change: number };
          sharpeRatio: { value: number; change: number };
          volatility: { value: number; change: number };
          maxDrawdown: { value: number; change: number };
        };
      }>('/admin/dashboard/fund-metrics');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        const data = response.data.data;
        const formattedMetrics: MetricProps[] = [
          {
            label: 'NAV',
            value: formatCurrency(data.nav.value),
            prefix: '$',
            change: data.nav.change,
          },
          {
            label: 'MTD Return',
            value: data.mtdReturn.value.toFixed(1),
            suffix: '%',
            change: data.mtdReturn.change,
          },
          {
            label: 'YTD Return',
            value: data.ytdReturn.value.toFixed(1),
            suffix: '%',
            change: data.ytdReturn.change,
          },
          {
            label: 'Sharpe Ratio',
            value: data.sharpeRatio.value.toFixed(2),
            change: data.sharpeRatio.change,
          },
          {
            label: 'Volatility',
            value: data.volatility.value.toFixed(1),
            suffix: '%',
            change: data.volatility.change,
          },
          {
            label: 'Max Drawdown',
            value: data.maxDrawdown.value.toFixed(1),
            suffix: '%',
            change: data.maxDrawdown.change,
          },
        ];
        setMetrics(formattedMetrics);
      } else if (response.status === 401) {
        console.error('Authentication required for fund metrics');
      } else if (response.status === 403) {
        console.error('Admin access required for fund metrics');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for fund metrics');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for fund metrics');
      } else {
        console.error('Error fetching fund metrics:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Refresh metrics every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  return (
    <div className={cn('glass-card rounded-xl p-6 animate-slide-up', className)}>
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center gap-3'>
          <div className='flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground'>
            <Activity className='w-5 h-5' />
          </div>
          <div>
            <h3 className='text-lg font-semibold text-foreground'>Fund Performance</h3>
            <p className='text-sm text-muted-foreground'>Real-time metrics</p>
          </div>
        </div>
        <div className='flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/10 border border-success/20'>
          <span className='w-2 h-2 rounded-full bg-success animate-pulse' />
          <span className='text-xs font-medium text-success'>Live</span>
        </div>
      </div>

      {loading ? (
        <div className='p-8 text-center text-muted-foreground'>
          <p>Loading metrics...</p>
        </div>
      ) : metrics.length === 0 ? (
        <div className='p-8 text-center text-muted-foreground'>
          <p>No metrics available</p>
        </div>
      ) : (
        <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className='p-4 rounded-xl bg-muted/30 border border-border/50 hover:border-primary/30 transition-all'
            >
              <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2'>
                {metric.label}
              </p>
              <div className='flex items-baseline gap-1'>
                {metric.prefix && (
                  <span className='text-lg text-muted-foreground'>{metric.prefix}</span>
                )}
                <span className='text-2xl font-bold text-foreground tracking-tight'>
                  {metric.value}
                </span>
                {metric.suffix && (
                  <span className='text-lg text-muted-foreground'>{metric.suffix}</span>
                )}
              </div>
              {metric.change !== undefined && (
                <div
                  className={cn(
                    'flex items-center gap-1 mt-2',
                    metric.change >= 0 ? 'text-success' : 'text-destructive'
                  )}
                >
                  {metric.change >= 0 ? (
                    <TrendingUp className='w-3 h-3' />
                  ) : (
                    <TrendingDown className='w-3 h-3' />
                  )}
                  <span className='text-xs font-medium'>
                    {metric.change >= 0 ? '+' : ''}
                    {metric.change.toFixed(2)}%
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
